import React, { PropsWithChildren, createContext, useContext, useRef } from 'react';
import { Toast, ToastMessage } from 'primereact/toast';
import { ConfirmDialog } from 'primereact/confirmdialog';

interface PopupContextValue {
  showToast: (message: ToastMessage | ToastMessage[]) => void;
}

const PopupContext = createContext<PopupContextValue | null>(null);

export const PopupProvider = ({ children }: PropsWithChildren) => {
  const toast = useRef<Toast>(null);
  const nextToastId = useRef(0);

  // Each message gets its own id. PrimeReact 10.8.5 removes an expired message by its _pId, but it reads the _pId 0 of the
  // first message of the page as "no id" and then removes every message with the same content: the second of two equal
  // toasts (two reservations saved in a row) vanished when the first one expired (#442).
  const showToast = (message: ToastMessage | ToastMessage[]) => {
    toast.current?.show({ life: 5000, id: `toast-${nextToastId.current++}`, ...message });
  };

  return (
    <PopupContext.Provider value={{ showToast }}>
      <Toast ref={toast} />
      <ConfirmDialog dismissableMask />
      {children}
    </PopupContext.Provider>
  );
};

// PopupProvider is the outermost provider of App (App.tsx), so every component and hook has it.
export const usePopups = () => useContext(PopupContext) as PopupContextValue;
